import * as React from "react";
import { Control, useController, useWatch } from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import PreferredValueWarning from "components/preferred-value-warning/preferred-value-warning.component";
import {
  selectCurrentRegion,
  selectCurrentUser
} from "features/auth/auth-slice";
import { CLINICS_QUERY, useGetClinicsQuery } from "services/clinics";
import { Clinic } from "services/types/clinics.type";

type ClinicFieldProps = {
  control: Control<any, any>;
  preferredClinic?: Clinic;
  name?: string;
  label?: string;
  sx?: SxProps;
  hideIcon?: boolean;
  required?: boolean;
};

export default function ClinicField({
  control,
  preferredClinic,
  sx,
  hideIcon,
  name = "clinic",
  label = "Clinic",
  required
}: ClinicFieldProps) {
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentUser = useAppSelector(selectCurrentUser);
  const staffMember = useWatch({
    control,
    name: "staff_member"
  });

  const { data: clinics, isLoading } = useGetClinicsQuery(
    {
      query: CLINICS_QUERY,
      region: currentRegion?.id,
      user: currentUser?.show_own_clinics_only
        ? currentUser?.id
        : currentRegion && !staffMember?.id
          ? currentUser?.id
          : undefined,
      staff_member: staffMember?.id
    },
    {
      skip: !currentUser?.id || currentUser?.id === -1
    }
  );

  const {
    field: { onChange, ...restField }
  } = useController({
    name,
    control
  });
  const filterOptions = createFilterOptions({
    stringify: (option: Clinic) => option.name
  });

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
      >
        {!hideIcon && (
          <LocationOnOutlinedIcon sx={{ mt: 1.5, mr: 1.5 }} color="action" />
        )}

        <Autocomplete
          disablePortal
          options={clinics || []}
          loading={isLoading}
          fullWidth
          popupIcon={<ExpandMoreOutlinedIcon />}
          getOptionLabel={({ name }) => name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          {...restField}
          filterOptions={filterOptions}
          renderInput={(params) => (
            <TextField {...params} required={required} label={label} />
          )}
          onChange={(_, data) => onChange(data)}
        />
      </Box>

      <PreferredValueWarning
        preferredValueTitle="clinic"
        patientPreferredValue={preferredClinic}
        selectedValue={restField?.value}
        sx={{ pl: 4 }}
      />
    </Box>
  );
}
